import { useMemo, useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import css from './index.module.scss';
import { seriesLinkProps } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
import Exposure from '@/utils/isInViewPort';
import Button from '@/components/UI/Button';
import EFArrowLeft from '@/components/UI/svg/Icons/EFArrowLeft';
import { createEFPic } from '@/utils/useEFPic';

const { EFPic } = createEFPic('/media/collection/');

const contentCacheMap: any[] = [];
export default function SeriesType({ contentData, serieType, lang }: any) {
    const { Link, gtm, useRouter } = useGlobal();
    const { locale } = useRouter();
    const [active, setActive] = useState<number>();
    const getData = (key: string) => lang[serieType] && lang[serieType][key];
    const processData = () => {
        for (let i = 0; i < contentCacheMap.length; i++) {
            if (isEqual(contentCacheMap[i], contentData)) {
                setActive(i);
                return;
            }
        }
        if (!contentData) return;
        contentCacheMap.push(contentData);
        setActive(contentCacheMap.length - 1);
    };

    // 修复白屏bug补充逻辑
    useEffect(() => {
        processData();
    }, [serieType, contentData]);

    useEffect(() => {
        let exposure: Exposure;
        setTimeout(() => {
            exposure = new Exposure({
                callback: (node: any) => {
                    const elements = document?.querySelectorAll('.series-container.show .ef-good-card');
                    const card = node?.closest('.ef-good-card');
                    const bannerName = card?.querySelector('.ef-good-card-title')?.innerHTML;
                    const position = Array.prototype.indexOf.call(elements, card);
                    // const { bannerName = '', position = '' } = node.dataset;
                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'view_banner',
                        banner_name: bannerName,
                        position: `banner_${position + 1}`,
                    });
                },
                once: true,
            });
            exposure?.observe('.isInViewPort');
        }, 2000);
        return () => {
            exposure?.disconnect();
            exposure = null;
        };
    }, [active]);

    useEffect(() => {
        // 点击埋点
        const container = document.querySelector('.series-container.show');
        const elements = document?.querySelectorAll('.series-container.show .ef-good-card');
        const gtmPush = (e: Event) => {
            const target = e.target as HTMLLinkElement;
            const card = target?.closest('.ef-good-card');
            const Link = target?.closest('a');
            const bannerName = card?.querySelector('.ef-good-card-title')?.innerHTML;
            const position = Array.prototype.indexOf.call(elements, card);

            if (!card || !Link) return;
            let button_name = 'Image';
            if (target.tagName !== 'IMG') {
                button_name = 'Learn more';
            }
            gtm.push({
                event: 'uaEvent',
                event_name: 'select_banner',
                banner_name: bannerName,
                position: `banner_${position + 1}`,
                button_name: button_name,
            });
        };
        container?.addEventListener('click', gtmPush);

        return () => container?.removeEventListener('click', gtmPush);
    }, [active]);

    const Dom = useMemo(() => {
        return (
            <>
                {contentCacheMap.map((contentData: any, index: number) => (
                    <div
                        key={contentData?.type + index}
                        style={{ display: active === index ? 'block' : 'none' }}
                        className={`${css.riverSeries} series-container ${active === index ? 'show' : 'hide'}`}
                    >
                        <div className="page-box1">
                            <div>
                                {contentData?.stage1?.map((item: any, itemIndex: number) => (
                                    <div className="ef-good-card" key={contentData?.type + index + itemIndex}>
                                        <div className="ef-good-card-msgs">
                                            <span
                                                className="ef-good-card-new-tips"
                                                style={{
                                                    visibility: item.isNew ? 'visible' : 'hidden',
                                                }}
                                            >
                                                {getData('new')}
                                            </span>
                                            <h1 className="ef-good-card-title">{getData(item.title)}</h1>
                                            <p
                                                className="ef-good-card-desc"
                                                style={{
                                                    display: item.subTitle ? 'block' : 'none',
                                                }}
                                            >
                                                {getData(item.subTitle)}
                                            </p>
                                            {item.detailsLink && (
                                                <div className="link ef-good-card-link">
                                                    <Link {...seriesLinkProps(item)} ariaLabel={getData(item.title)}>
                                                        <Button
                                                            type="link"
                                                            aria-label={getData(item.detailText)}
                                                            className="link-btn font-family-medium ef-good-card-link-in1"
                                                        >
                                                            {getData(item.detailText)}
                                                            <span className="more ef-good-card-link-more">
                                                                <EFArrowLeft />
                                                            </span>
                                                        </Button>
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                        <span className="ef-good-card-full-bg">
                                            <EFPic
                                                name={[`${item.images}.webp`, `${item.images}.png`]}
                                                alt={getData(item.title)}
                                                className="fullImgs isInViewPort"
                                                data-banner-name={getData(item.title)}
                                                data-position={`banner_${getData(item.title)}_${itemIndex + 1}`}
                                            />
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {contentData?.stage2?.map((item: any, itemIndex: number) => (
                            <section key={item.bigTitle + index + itemIndex} className="section">
                                <div className="series-title series-title1">
                                    <h2>{getData(item.bigTitle)}</h2>
                                </div>
                                {item.bigArr?.map((biglist: any, listIndex: number) => (
                                    <div className="page-box2" key={biglist.title + index + itemIndex + listIndex}>
                                        <div>
                                            <div className="ef-good-card">
                                                <div className="ef-good-card-msgs">
                                                    <span
                                                        className="new font-family-extralight ef-good-card-new-tips"
                                                        style={{
                                                            visibility: biglist.isNew ? 'visible' : 'hidden',
                                                        }}
                                                    >
                                                        {getData('new')}
                                                    </span>
                                                    <h2 className="title ef-good-card-title">{getData(biglist.title)}</h2>
                                                    <p
                                                        className="subtitle font-family-semibold ef-good-card-desc"
                                                        style={{
                                                            display: biglist.subTitle ? 'block' : 'none',
                                                        }}
                                                    >
                                                        {getData(biglist.subTitle)}
                                                    </p>
                                                    {biglist.detailsLink && (
                                                        <div className="link ef-good-card-link">
                                                            <Link {...seriesLinkProps(biglist)} ariaLabel={getData(biglist.title)}>
                                                                <Button
                                                                    type="link"
                                                                    aria-label={getData(biglist.detailText)}
                                                                    className="link-btn font-family-medium"
                                                                >
                                                                    <div className="text-btn">
                                                                        <span className="text">{getData(biglist.detailText)}</span>
                                                                        <span className="more ef-good-card-link-more">
                                                                            <EFArrowLeft />
                                                                        </span>
                                                                    </div>
                                                                </Button>
                                                            </Link>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="ef-good-card-full-bg">
                                                    <Link {...seriesLinkProps(biglist)} ariaLabel={getData(biglist.title)}>
                                                        <EFPic
                                                            name={[`${biglist.images}.webp`, `${biglist.images}.png`]}
                                                            className="fullImgs isInViewPort"
                                                            data-banner-name={getData(biglist.title)}
                                                            data-position={`banner_${getData(item.bigTitle)}_${listIndex + 1}`}
                                                            alt={getData(biglist.title)}
                                                        />
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="page-box3">
                                    <ul>
                                        {item.arr?.map((list: any, listIndex: number) => (
                                            <li key={getData(list.title) + index + listIndex}>
                                                <a href="" aria-label={getData(list.title)}>
                                                    <div className="ef-good-card">
                                                        <div className="ef-good-card-msgs">
                                                            <span
                                                                className="ef-good-card-new-tips"
                                                                style={{
                                                                    visibility: list.isNew ? 'visible' : 'hidden',
                                                                }}
                                                            >
                                                                {getData('new')}
                                                            </span>
                                                            <h2 className="ef-good-card-title">{getData(list.title)}</h2>
                                                            <p
                                                                className="ef-good-card-desc"
                                                                style={{
                                                                    display: list.subTitle ? 'block' : 'none',
                                                                }}
                                                            >
                                                                {getData(list.subTitle)}
                                                            </p>
                                                            {list.detailsLink && (
                                                                <div className="ef-good-card-link">
                                                                    <Link {...seriesLinkProps(list)} ariaLabel={getData(list.title)}>
                                                                        <Button
                                                                            type="link"
                                                                            aria-label={getData(list.detailText)}
                                                                            className="link-btn small-link-btn font-family-medium"
                                                                        >
                                                                            {getData(list.detailText)}
                                                                            <span className="ef-good-card-link-more">
                                                                                <EFArrowLeft />
                                                                            </span>
                                                                        </Button>
                                                                    </Link>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <Link {...seriesLinkProps(list)} ariaLabel={getData(list.title)}>
                                                            <span className="ef-good-card-full-bg">
                                                                <EFPic
                                                                    name={[`${list.images}.webp`, `${list.images}.png`]}
                                                                    className={`fullImgs isInViewPort ${
                                                                        list.images === '/img/mobile/collections/solar/solar-110.png' && locale === 'jp'
                                                                            ? 'contain'
                                                                            : ''
                                                                    }`}
                                                                    data-banner-name={getData(list.title)}
                                                                    data-position={`banner_${getData(item.bigTitle)}_${
                                                                        listIndex + 1 + (item.bigArr?.length || 0)
                                                                    }`}
                                                                    alt={getData(list.title)}
                                                                />
                                                            </span>
                                                        </Link>
                                                    </div>
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        ))}
                        {contentData?.stage3?.map((item: any, itemIndex: number) => (
                            <section key={item.title + index + itemIndex}>
                                <div className="series-title series-title2">
                                    <h2>{getData(item.title)}</h2>
                                </div>
                                <div className="page-box5">
                                    <ul>
                                        {item.arr?.map((list: any, listIndex: number) => (
                                            <li key={list.title + index + itemIndex + listIndex}>
                                                <div className="ef-good-card">
                                                    <Link {...seriesLinkProps(list)} ariaLabel={getData(list.title)}>
                                                        <span className="ef-good-card-img">
                                                            <EFPic
                                                                name={[`${list.images}.webp`, `${list.images}.png`]}
                                                                className="fullImgs isInViewPort"
                                                                data-banner-name={getData(list.title)}
                                                                data-position={`banner_${getData(item.title)}_${listIndex + 1}`}
                                                                alt={getData(list.title)}
                                                            />
                                                        </span>
                                                    </Link>
                                                    <div className="ef-good-card-msgs">
                                                        <div className="ef-good-card-new-tips"></div>
                                                        <h2 className="ef-good-card-title">{getData(list.title)}</h2>
                                                        <p
                                                            className="ef-good-card-desc"
                                                            style={{
                                                                display: list.subTitle ? 'block' : 'none',
                                                            }}
                                                        >
                                                            {getData(list.subTitle)}
                                                        </p>
                                                        {list.detailsLink && (
                                                            <div className="ef-good-card-link">
                                                                <Link {...seriesLinkProps(list)} ariaLabel={getData(list.title)}>
                                                                    <Button
                                                                        type="link"
                                                                        aria-label={getData(list.detailText)}
                                                                        className="link-btn font-family-medium"
                                                                    >
                                                                        {getData(list.detailText)}
                                                                        <span className="ef-good-card-link-more">
                                                                            <EFArrowLeft />
                                                                        </span>
                                                                    </Button>
                                                                </Link>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        ))}
                        {contentData?.stage4?.map((item: any, itemIndex: number) => (
                            <section className="page-box4" key={item.title + index + itemIndex}>
                                <div className="ef-good-card">
                                    <div className="ef-good-card-full-bg">
                                        <EFPic
                                            name={[`${item.images}.webp`, `${item.images}.png`]}
                                            className="fullImgs isInViewPort"
                                            data-banner-name={getData(item.title)}
                                            data-position={`banner_${getData(item.title)}_${itemIndex + 1}`}
                                            alt={getData(item.title)}
                                        />
                                    </div>
                                    <div className="ef-good-card-msgs">
                                        <div>
                                            <div className="ef-good-card-new-tips"></div>
                                            <h2 className="ef-good-card-title">{getData(item.title)}</h2>
                                            <p
                                                className="ef-good-card-desc"
                                                style={{
                                                    display: item.subTitle ? 'block' : 'none',
                                                }}
                                            >
                                                {getData(item.subTitle)}{' '}
                                            </p>
                                            <div className="ef-good-card-link">
                                                <Link {...seriesLinkProps(item)} ariaLabel={getData(item.title)}>
                                                    <Button type="link" aria-label={getData(item.detailText)} className="link-btn font-family-medium">
                                                        {getData(item.detailText)}
                                                        <span className="more ef-good-card-link-more">
                                                            <svg width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
                                                                <path
                                                                    d="M4.907 8.907 9 5 4.907 1.093"
                                                                    stroke="currentColor"
                                                                    strokeWidth="1.2"
                                                                    fill="none"
                                                                    fillRule="evenodd"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </span>
                                                    </Button>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        ))}
                    </div>
                ))}
            </>
        );
    }, [active]);

    return <>{Dom}</>;
}
